
import { useEffect, useId, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  buildLinearGradient,
  cssToHex,
  detectColorFieldMode,
  hexToRgba,
  parseLinearGradient,
  parseRgba,
} from '@/lib/pstyle-color-utils';
import { cn } from '@/lib/utils';

type PstyleColorFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Permite opacidade no modo sólido (rgba). */
  allowAlpha?: boolean;
};

const colorInputClass =
  'h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-white/15 bg-white/[0.05] p-1';

export function PstyleColorField({
  label,
  value,
  onChange,
  placeholder,
  allowAlpha = false,
}: PstyleColorFieldProps) {
  const labelId = useId();
  const [mode, setMode] = useState<'solid' | 'gradient'>(() => detectColorFieldMode(value));

  useEffect(() => {
    setMode(detectColorFieldMode(value));
  }, [value]);

  const rgba = allowAlpha ? parseRgba(value) : null;
  const solidHex = rgba?.hex ?? cssToHex(value);
  const solidAlpha = rgba?.alpha ?? 1;

  const gradient = parseLinearGradient(value) ?? {
    angle: 225,
    color1: cssToHex(value, '#633775'),
    color2: cssToHex(value, '#361e40'),
  };

  const previewBackground = value.trim() || 'transparent';

  const handleModeChange = (next: string) => {
    if (next !== 'solid' && next !== 'gradient') return;
    setMode(next);
    if (next === 'gradient' && !parseLinearGradient(value)) {
      onChange(buildLinearGradient(225, solidHex, solidHex));
    }
    if (next === 'solid' && parseLinearGradient(value)) {
      onChange(allowAlpha ? hexToRgba(gradient.color1, solidAlpha) : gradient.color1);
    }
  };

  return (
    <div className="space-y-2">
      <Label id={labelId} className="font-poppins text-slate-300">
        {label}
      </Label>

      <div
        className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
        role="group"
        aria-labelledby={labelId}
      >
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 shrink-0 rounded-lg border border-white/20 shadow-inner ring-1 ring-black/20"
            style={{ background: previewBackground }}
            aria-hidden
          />
          <Tabs value={mode} onValueChange={handleModeChange} className="min-w-0 flex-1">
            <TabsList className="grid h-9 w-full grid-cols-2 bg-white/10">
              <TabsTrigger
                value="solid"
                className="font-inter text-xs data-[state=active]:bg-white/15 data-[state=active]:text-white"
              >
                Sólida
              </TabsTrigger>
              <TabsTrigger
                value="gradient"
                className="font-inter text-xs data-[state=active]:bg-white/15 data-[state=active]:text-white"
              >
                Degradê
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {mode === 'solid' ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={solidHex}
                onChange={(e) => {
                  const hex = e.target.value;
                  onChange(allowAlpha ? hexToRgba(hex, solidAlpha) : hex);
                }}
                className={colorInputClass}
                aria-label={`Cor sólida — ${label}`}
              />
              <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={cn(painelInputClass, 'min-w-0 flex-1 font-mono text-xs')}
                placeholder={placeholder ?? '#361e40'}
              />
            </div>
            {allowAlpha ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between font-inter text-xs text-slate-400">
                  <span>Opacidade</span>
                  <span>{Math.round(solidAlpha * 100)}%</span>
                </div>
                <Slider
                  value={[solidAlpha]}
                  min={0}
                  max={1}
                  step={0.01}
                  onValueChange={([alpha]) => onChange(hexToRgba(solidHex, alpha ?? 1))}
                  className="py-1 [&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:border-sky-400/60"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <span className="font-inter text-xs text-slate-400">Cor 1</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={gradient.color1}
                    onChange={(e) =>
                      onChange(buildLinearGradient(gradient.angle, e.target.value, gradient.color2))
                    }
                    className={colorInputClass}
                    aria-label={`Cor 1 do degradê — ${label}`}
                  />
                  <span className="font-mono text-xs text-slate-500">{gradient.color1}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="font-inter text-xs text-slate-400">Cor 2</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={gradient.color2}
                    onChange={(e) =>
                      onChange(buildLinearGradient(gradient.angle, gradient.color1, e.target.value))
                    }
                    className={colorInputClass}
                    aria-label={`Cor 2 do degradê — ${label}`}
                  />
                  <span className="font-mono text-xs text-slate-500">{gradient.color2}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between font-inter text-xs text-slate-400">
                <span>Ângulo</span>
                <span>{Math.round(gradient.angle)}°</span>
              </div>
              <Slider
                value={[gradient.angle]}
                min={0}
                max={360}
                step={1}
                onValueChange={([angle]) =>
                  onChange(buildLinearGradient(angle ?? 0, gradient.color1, gradient.color2))
                }
                className="py-1 [&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:border-sky-400/60"
              />
            </div>

            <input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={cn(painelInputClass, 'font-mono text-xs')}
              placeholder={placeholder ?? 'linear-gradient(225deg, #633775, #361e40)'}
            />
          </div>
        )}
      </div>
    </div>
  );
}
